import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import {
  selectConnectionTypes,
  selectConnectionTypesIsEmpty,
  selectConnectionTypesLoading,
} from 'store/features/connection-types/selector';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import connectionTypesActions from 'store/features/connection-types/actions';
import { CreateConnectionTypeDialog, UpdateConnectionTypeDialog } from './components';

const ConnectionTypes = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectConnectionTypesLoading);
  const connectionTypes = useAppSelector(selectConnectionTypes);
  const isEmptyConnectionTypes = useAppSelector(selectConnectionTypesIsEmpty);

  // useState hooks
  const [openDeleteConnectionType, setOpenDeleteConnectionType] = useState(false);
  const [openCreateConnectionType, setOpenCreateConnectionType] = useState(false);
  const [openUpdateConnectionType, setOpenUpdateConnectionType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'connectionTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(connectionTypesActions.getConnectionTypes());
  };

  // sync helper functions
  const handleCloseDeleteConnectionType = () => setOpenDeleteConnectionType(false);
  const handleClickOpenDeleteConnectionType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteConnectionType(true);
  };

  const handleCloseCreateConnectionType = () => setOpenCreateConnectionType(false);
  const handleClickOpenCreateConnectionType = () => setOpenCreateConnectionType(true);

  const handleCloseUpdateConnectionType = () => setOpenUpdateConnectionType(false);
  const handleClickOpenUpdateConnectionType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateConnectionType(true);
  };

  // useEffect hooks
  useEffect(() => {
    if (!connectionTypes.length && !isEmptyConnectionTypes) {
      dispatch(connectionTypesActions.getConnectionTypes());
    }
  }, [connectionTypes, isEmptyConnectionTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-connection-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateConnectionType}>
              <FormattedMessage id="connectionTypes.createConnectionType" defaultMessage="Bağlantı Türü Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-connection-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateConnectionType}>
              <FormattedMessage id="connectionTypes.updateConnectionType" defaultMessage="Bağlantı Türü Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-connection-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteConnectionType}>
              <FormattedMessage id="connectionTypes.deleteConnectionType" defaultMessage="Bağlantı Türü Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateConnectionTypeDialog
        open={openCreateConnectionType}
        handleClose={handleCloseCreateConnectionType}
        refresh={refresh}
      />
      <UpdateConnectionTypeDialog
        open={openUpdateConnectionType}
        connectionTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateConnectionType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteConnectionType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteConnectionType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="connectionTypes.deleteConnectionType" defaultMessage="Bağlantı Türü Sil" />}
        subTitle={
          <FormattedMessage
            id="connectionTypes.deleteConnectionTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra bağlantı türü veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteConnectionType}
        successMessage={
          <FormattedMessage
            id="connectionTypes.deleteConnectionTypeCompleted"
            defaultMessage="Seçili bağlantı türünü silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={connectionTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default ConnectionTypes;
